import React from 'react';

const FRAME_BORDER = '1px solid #d1d5db';
const PANEL_BORDER = '1px solid #d1d5db';

function LockIcon({ color, size = 24 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      width={size}
      height={size}
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      <circle cx="12" cy="16" r="1" fill={color} stroke="none" />
    </svg>
  );
}

function PlaceholderContent() {
  return (
    <div style={{ padding: 20, minHeight: 320, backgroundColor: '#fafafa' }}>
      <div style={{ height: 14, width: '55%', backgroundColor: '#d1d5db', borderRadius: 4, marginBottom: 12 }} />
      <div style={{ height: 10, width: '72%', backgroundColor: '#e5e7eb', borderRadius: 4, marginBottom: 8 }} />
      <div style={{ height: 10, width: '64%', backgroundColor: '#e5e7eb', borderRadius: 4, marginBottom: 16 }} />
      <div style={{ height: 140, backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8 }} />
    </div>
  );
}

/**
 * Frosted overlay gate for unapproved / insufficient-permission states.
 * Blurs underlying content and shows lock + message (not a red error banner).
 */
export default function PermissionGate({
  blocked,
  children,
  title = 'Insufficient permission',
  message = 'This action is available after your account is approved. You can still browse the catalog while you wait.',
  hint = 'Approval usually within one business day',
  primaryColor = '#0066cc',
  minHeight = 400,
  style,
}) {
  if (!blocked) {
    return children ?? null;
  }

  const visibleChildren = React.Children.toArray(children).filter(
    (child) => child !== null && child !== false && child !== undefined,
  );

  const backdrop = visibleChildren.length > 0 ? visibleChildren : <PlaceholderContent />;

  return (
    <div
      style={{
        position: 'relative',
        minHeight,
        borderRadius: 12,
        border: FRAME_BORDER,
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        overflow: 'hidden',
        ...style,
      }}
    >
      <div style={{ pointerEvents: 'none', userSelect: 'none', minHeight }}>{backdrop}</div>

      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '28px 20px 32px',
          boxSizing: 'border-box',
          background: 'rgba(229, 231, 235, 0.72)',
          backdropFilter: 'blur(12px) saturate(120%)',
          WebkitBackdropFilter: 'blur(12px) saturate(120%)',
        }}
      >
        <div
          style={{
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: 300,
            textAlign: 'center',
            padding: '24px 20px 28px',
            borderRadius: 10,
            border: PANEL_BORDER,
            background: '#ffffff',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
            }}
          >
            <LockIcon color={primaryColor} />
          </div>

          <h3
            style={{
              margin: 0,
              fontSize: 17,
              fontWeight: 700,
              color: '#111827',
              lineHeight: 1.3,
            }}
          >
            {title}
          </h3>

          <p
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: 1.55,
              color: '#111827',
            }}
          >
            {message}
          </p>

          {hint ? (
            <div
              style={{
                marginTop: 8,
                width: 118,
                height: 118,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 14,
                boxSizing: 'border-box',
                backgroundColor: primaryColor,
                color: '#ffffff',
                fontSize: 10,
                fontWeight: 600,
                lineHeight: 1.4,
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0, 102, 204, 0.25)',
              }}
            >
              {hint}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
